import { Trans, useLingui } from "@lingui/react/macro";
import React, { useEffect, useMemo } from "react";
import { graphql, HeadProps, PageProps, Slice } from "gatsby";
import { DestinationPageNewQuery } from "types/graphql-types";
import Herospace from "src/page-components/destinations/Herospace";
import FindYourCruise from "components/FindYourCruise";

import TravelGuide from "src/page-components/destinations/TravelGuide/TravelGuide";
import {
  Button,
  ButtonsContainer,
  DescriptionText,
  DescriptionTextContent,
  DescriptionTextLeadIn,
  SectionContainer,
  SectionTitle,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import ReadMore from "components/ReadMore/ReadMore";
import Slider, { SliderSlide } from "components/Slider";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import TextBox from "components/TextBox/TextBox";
import { arrayIsNotEmpty, isPresent } from "src/utils/checks";
import PageBuilderBlogMosaic from "components/PageBuilder/PageBuilderBlogMosaic";
import { PageContext } from "types/pageContext";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import StickyMenu, { StickyMenuProps } from "components/StickyMenu/StickyMenu";
import { destinationToDataLayer } from "src/utils/analytics/tracking";
import SectionSlider, {
  SectionSliderItem
} from "components/SectionSlider/SectionSlider";
import ShipsSection from "src/page-components/destinations/ShipsSection";
import ImmersivePictureCard from "components/Card/ImmersivePictureCard/ImmersivePictureCard";
import EmotionalHeroBanner from "components/EmotionalHeroBanner/EmotionalHeroBanner";
import { RAQButton } from "components/ECommerceButtons";
import { useClientSideFeatureFlag } from "src/utils/feature-flags";

const PageBuilderEmotionalLauncher = React.lazy(
  () => import("src/components/PageBuilder/PageBuilderEmotionalLauncher")
);

const DestinationCruisesSlider = React.lazy(
  () => import("src/page-components/destinations/DestinationCruisesSlider")
);

const ExpeditionTeamMembers = React.lazy(
  () => import("src/page-components/cruise/ExpeditionTeamMembers")
);

type RealDestinationPageQuery = DestinationPageNewQuery & {
  destination: NonNullable<DestinationPageNewQuery["destination"]>;
};

const DestinationPageTemplate: React.FC<
  React.PropsWithChildren<PageProps<RealDestinationPageQuery>>
> = (props) => {
  const { t } = useLingui();

  const destinationData = props.data.destination;
  const subDestinations = props.data.subDestinations?.nodes;
  const hasSubDestinations =
    subDestinations &&
    subDestinations.length > 0 &&
    subDestinations.some((d) => d.fullPath);
  const { destinationWebCode, blog, slideshowImages = [] } = destinationData;
  const ships = destinationData.visitingShips?.filter(isPresent);

  const fycQuery = new FYCQueryBuilder()
    .filterByDestinationWebCode(destinationWebCode)
    .generate();

  const hasShipSection = ships && ships.length > 0;
  const hasBlogSection = blog && blog.blogPosts?.length > 0;
  const expeditionTeam = destinationData.expeditionTeam?.filter(isPresent);
  const withExpeditionTeam = arrayIsNotEmpty(expeditionTeam);
  const withSilverseaHotel = isPresent(
    destinationData.silverseaHotelSectionContent?.emotionalSection
  );
  const withAntarcticaFlySection = isPresent(
    destinationData.antarcticaFlySectionContent?.emotionalSection
  );

  const menuSections: StickyMenuProps["sectionRefs"] = [
    withAntarcticaFlySection
      ? {
          title: t`Antarctica Fly`,
          id: "antarctica-fly",
          trackingName: "antarctica-fly"
        }
      : undefined,
    {
      id: "fyc",
      title: t`Itineraries`,
      trackingName: "fyc"
    },
    {
      id: "gallery",
      title: t`Gallery`,
      trackingName: "gallery"
    },
    withSilverseaHotel
      ? {
          title: t`Hotel`,
          id: "silversea-hotel",
          trackingName: "silversea-hotel"
        }
      : undefined,
    {
      id: "travel_guide",
      title: t`Brochure`,
      trackingName: "brochure"
    },
    withExpeditionTeam
      ? {
          title: t`Expedition Team`,
          id: "expeditionTeam",
          trackingName: "expedition-team"
        }
      : undefined
  ].filter(isPresent);

  if (hasBlogSection)
    menuSections.push({
      id: "blog",
      title: t`Stories`,
      trackingName: "blog"
    });

  if (hasShipSection)
    menuSections.push({
      id: "luxuryShips",
      title: t`Ships`,
      trackingName: "ships"
    });

  useEffect(() => {
    destinationToDataLayer({
      name: destinationData.name?.en,
      destination_id: destinationData.destinationId,
      destination_type: destinationData.isExpeditionDestination
        ? "Expedition"
        : "Classic"
    });
  }, [
    destinationData.destinationId,
    destinationData.isExpeditionDestination,
    destinationData.name?.en
  ]);

  const sectionSliderImages = useMemo(
    () =>
      slideshowImages
        .map<SectionSliderItem | undefined>((image) => {
          const { imageWithCaption, _key } = image || {};
          if (!isPresent(imageWithCaption)) return undefined;

          return {
            _key: _key,
            description: imageWithCaption.caption?.localized,
            image: imageWithCaption
          };
        })
        .filter(isPresent),
    [slideshowImages]
  );

  const destinationName = destinationData.name?.localized;

  const ctas = (
    <ButtonsContainer align="center">
      {props.data.raqPagePath && (
        <RAQButton
          variant="primary"
          url={props.data.raqPagePath}
          size="regular"
          inverted={true}
        />
      )}

      <Button
        renderAs="a"
        href="#fyc"
        variant="accent"
        dataContentPiece="anchor_book_your_cruise_button"
      >
        <Trans id="ecommerce-button-label">Book your Cruise</Trans>
      </Button>
    </ButtonsContainer>
  );

  return (
    <React.Fragment>
      <div id="root">
        {useClientSideFeatureFlag("emotionalHeroDestinationPage") ? (
          <EmotionalHeroBanner
            title={destinationData.name?.localized}
            content={destinationData.title?.localized}
            mediaType={destinationData.mediaType}
            videoId={destinationData.video?.file?.videoId}
            backgroundImage={destinationData.heroImage}
            ctaButtons={ctas}
            customMargin={true}
          />
        ) : (
          <Herospace
            heroImage={destinationData.heroImage}
            name={destinationData.name?.localized}
            title={destinationData.title?.localized}
            raqPagePath={props.data.raqPagePath}
          />
        )}

        <StickyMenu
          sectionRefs={menuSections}
          translateDesktopTop={32}
          backgroundColor="white"
        />

        <SectionContainer
          backgroundColor="white"
          spacingTop="regular"
          spacingBottom="regular"
          dataContentName="destination_introduction"
        >
          <DescriptionText>
            <DescriptionTextLeadIn>
              {destinationData.leadIn?.localized}
            </DescriptionTextLeadIn>
            <DescriptionTextContent>
              <ReadMore
                backgroundColor="white"
                height="200px"
                hideCloseButton
                enableInDesktop
                openLabel={
                  <Trans id="Keep Reading (read more)">Keep Reading</Trans>
                }
                closeLabel={<Trans id="Close (read more)">Close</Trans>}
              >
                <TextBox
                  fontSize="medium"
                  content={destinationData.body?.localized}
                />
              </ReadMore>
            </DescriptionTextContent>
          </DescriptionText>
        </SectionContainer>

        <React.Suspense>
          <DestinationCruisesSlider
            destinationWebCode={destinationWebCode}
            promosContent={props.data.cruisePromosContent}
            shipsContent={props.data.shipsContent}
            backgroundColor="neutral01"
          />
        </React.Suspense>

        {withAntarcticaFlySection && (
          <div id="antarctica-fly">
            <React.Suspense>
              <PageBuilderEmotionalLauncher
                {...destinationData.antarcticaFlySectionContent
                  ?.emotionalSection}
              />
            </React.Suspense>
          </div>
        )}

        <FindYourCruise
          title={<Trans>Find your cruise</Trans>}
          showTitle={true}
          titleAlign="left"
          backgroundColor="neutral01"
          initialSearchQuery={fycQuery}
          resultsPageSize={6}
          showExpeditionFilter={
            destinationData.fycShowExpeditionFilter || false
          }
          specialTypeFilter={destinationData.fycSpecialType}
          showDestinationFilter={false}
          showShipFilter={true}
          showDurationFilter={true}
          showPromosFilter={true}
          showFeaturesFilter={true}
          showCruiseEventsFilter={true}
          firstLastDepartureCruiseDates={
            props.data.firstLastDepartureCruiseDates
          }
          promosContent={props.data.cruisePromosContent}
          shipsContent={props.data.shipsContent}
          cruisesTagCategoryContent={props.data.cruisesTagCategoryContent}
          themedCruisesContent={props.data.themedCruisesContent}
          showLastSeenProducts
          lastSeenProductsFilter={{ destinationWebCode: destinationWebCode }}
          lastSeenProductSpacingTop="compact"
        />

        {arrayIsNotEmpty(slideshowImages) && (
          <SectionContainer
            backgroundColor="white"
            spacingTop="regular"
            spacingBottom="regular"
            id="gallery"
          >
            <SectionTitle variant="wide">
              <SectionTitleHeading>
                <Trans id="destination-gallery-title">
                  {destinationName} dreams
                </Trans>
              </SectionTitleHeading>
            </SectionTitle>

            <SectionSlider items={sectionSliderImages}></SectionSlider>
          </SectionContainer>
        )}

        {withSilverseaHotel && (
          <div id="silversea-hotel">
            <React.Suspense>
              <PageBuilderEmotionalLauncher
                {...destinationData.silverseaHotelSectionContent
                  ?.emotionalSection}
              />
            </React.Suspense>
          </div>
        )}

        <TravelGuide
          sectionTitle={destinationData?.travelGuideTitle?.localized}
          image={destinationData?.travelGuideImage}
          guideTitle={destinationData?.travelGuideHeader?.localized}
          content={destinationData?.travelGuideBody?.localized}
          brochureGroup={destinationData.brochureGroup}
          ctaText={destinationData?.travelGuideButtonText?.localized}
        />

        {withExpeditionTeam && (
          <React.Suspense>
            <ExpeditionTeamMembers
              id="expeditionTeam"
              title={
                props.data.microContent?.expeditionTeamSection?.title?.localized
              }
              content={
                props.data.microContent?.expeditionTeamSection?.content
                  ?.localized
              }
              expeditionTeam={expeditionTeam}
            />
          </React.Suspense>
        )}

        {hasBlogSection && (
          <div id="blog">
            <SectionContainer spacingTop="regular" spacingBottom="regular">
              <PageBuilderBlogMosaic
                content={blog}
                cardBackgroundColor="neutral05"
              />
            </SectionContainer>
          </div>
        )}

        {hasShipSection && (
          <div id="luxuryShips" data-content-name="ship_cards">
            <ShipsSection
              title={destinationData.shipsSectionTitle?.localized}
              ships={ships}
            />
          </div>
        )}

        {hasSubDestinations && (
          <div
            id="relatedDestinations"
            data-content-name="sub_destinations_slider"
          >
            <SectionContainer
              backgroundColor="white"
              spacingTop="regular"
              spacingBottom="regular"
            >
              <SectionTitle variant="wide">
                <SectionTitleHeading>
                  <Trans>Related Destinations</Trans>
                </SectionTitleHeading>
              </SectionTitle>
              <Slider variantColor="white">
                {subDestinations.map((subDestination) => {
                  const card = subDestination.card;
                  if (!card) return null;
                  return (
                    <SliderSlide key={subDestination.id}>
                      <ImmersivePictureCard
                        action={{ href: subDestination.fullPath }}
                        trackingName={
                          subDestination.metadata?.tracking?.name || ""
                        }
                        title={card.title?.localized}
                        altText={card.image?.alt?.localized}
                        image={card.image}
                        key={subDestination.id}
                      />
                    </SliderSlide>
                  );
                })}
              </Slider>
            </SectionContainer>
          </div>
        )}
        <Slice allowEmpty alias="subscribe-to-newsletter" />
      </div>
    </React.Fragment>
  );
};

export const Head = (
  props: HeadProps<RealDestinationPageQuery, PageContext>
) => {
  const { data, pageContext } = props;
  const destinationData = data.destination;

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={destinationData.metadata?.openGraph}
      excludeFromSitemap={destinationData.metadata?.excludeFromSitemap}
    />
  );
};

export const query = graphql`
  fragment VisitingShip on SanityShip {
    id
    card {
      ...AsCard
    }
    fullPath
    versions {
      validFrom
      shipType
      crewCount
      _rawDescription
      suiteCount
      diningOptions
      image {
        ...ImageWithPreview
      }
      deckPlanPdf {
        asset {
          url
        }
      }
      lastRefurbishmentYear
      passengerCount
      facilities {
        _key
        name {
          localized
        }
      }
    }
    name
  }

  fragment AdditionalCruiseGroup on SanityHighlightedCruiseGroup {
    card {
      ...AsCard
    }
    metadata {
      tracking {
        name
      }
    }

    fullPath
  }

  query DestinationPageNew($id: String!) {
    raqPagePath
    cruisePromosContent {
      ...FYCPromosContent
    }
    shipsContent {
      ...FYCShipsContent
    }
    themedCruisesContent {
      ...FYCThemedCruisesContent
    }
    cruisesTagCategoryContent {
      ...FYCCruisesTagCategoryContent
    }
    firstLastDepartureCruiseDates: firstLastDepartureCruiseDates {
      minYearMonth
      maxYearMonth
    }
    destination: sanityDestination(id: { eq: $id }) {
      cruiseGroup
      additionalCruiseGroup {
        ...AdditionalCruiseGroup
      }
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      destinationId
      destinationWebCode
      name {
        localized
        en
      }
      title {
        localized
      }
      mediaType
      heroImage {
        ...HeroResponsiveImage
      }
      video {
        file {
          videoId
          previewImageUrl
          posterImageDownloadUrl
        }

        trackingTitle
      }
      leadIn {
        localized
      }
      body {
        localized
      }

      slideshowImages {
        _key
        imageWithCaption {
          ...ImageWithCaptionAndPreview
        }
      }
      travelGuideBody {
        localized
      }
      travelGuideButtonText {
        localized
      }
      travelGuideHeader {
        localized
      }

      travelGuideImage {
        ...Image
      }
      travelGuideTitle {
        localized
      }
      brochureGroup {
        ...BrochureGrouping
      }

      packagesTitle {
        localized
      }
      packagesHeaderContent {
        localized
      }
      allInclusiveDisclaimer {
        localized
      }

      shipsSectionTitle {
        localized
      }
      visitingShips {
        ...VisitingShip
      }
      blog {
        ...PageBuilderBlogSection
      }
      fycShowExpeditionFilter
      fycSpecialType

      inclusionsContent {
        ...InfographicStepInclusionContent
      }
      isExpeditionDestination
      expeditionTeam {
        ...ExpeditionTeamMember
      }
      silverseaHotelSectionContent {
        emotionalSection {
          ...PageBuilderEmotionalLauncher
        }
      }
      antarcticaFlySectionContent {
        emotionalSection {
          ...PageBuilderEmotionalLauncher
        }
      }
    }
    subDestinations: allSanitySubDestination(
      sort: { card: { title: { en: ASC } } }
      filter: { destination: { id: { eq: $id } } }
    ) {
      nodes {
        id
        fullPath
        card {
          ...AsCard
        }
        metadata {
          tracking {
            name
          }
        }
      }
    }
    cancellationPolicyModalContent: sanityCancellationPolicyModal(
      _id: { regex: "/cancellationPolicyModal$/" }
    ) {
      ...CancellationPolicyModalContent
    }

    faresContent: allSanityFarePage {
      nodes {
        fareCodes
        ...InfographicBoxContent
      }
    }

    subscribeFormContent: subscribeToNewsletterContent {
      heading {
        title {
          localized
        }
        content {
          localized
        }
      }

      backgroundImage {
        ...ImageWithAlt
      }
    }

    microContent: cruisePageMicroContent {
      expeditionTeamSection {
        title {
          localized
        }
        content {
          localized
        }
      }
    }
  }

  fragment BrochureGrouping on SanityBrochureGrouping {
    fullPath
    brochures {
      ...Brochure
    }
    formDisclaimerContent: formDisclaimer {
      ...FormDisclaimer
    }
  }
`;

export default DestinationPageTemplate;
